import { booksCompleteRequest, booksUpdateMany, getSummaryRequest } from "@backend/BooksRequests";
import { objectToQueryString } from "@helpers/utils/utils";
import { hasValue } from "@helpers/validations/common";
import { useSelector } from "react-redux";

function StudentHomePageViewModel() {
    // @ts-ignore
    const userData = useSelector((state) => state.user.credentials);

    /**
     * Checks if a notification should be shown based on user data and sets the notification modal visibility.
     *
     * @param {Function} setShowNotificationModal - Function to set the visibility of the notification modal.
     * @returns {void}
     */
    function checkNotification(setShowNotificationModal) {
        if (userData.showAdvice) {
            setShowNotificationModal(true);
        }
    }

    /**
     * Fetches the summary data and updates the state accordingly.
     *
     * @param {Function} setBooks - Function to update the books state.
     * @param {Function} setDates - Function to update the dates state.
     * @param {Function} setBarcode - Function to update the barcode state.
     * @param {Function} setLoading - Function to update the loading state.
     * @returns {Promise<void>} - A promise that resolves when the summary data has been fetched and state updated.
     */
    async function getSummary(setBooks, setDates, setBarcode, setLoading) {
        if (hasValue(userData.currentBarcode)) {
            const params = {
                barcode: userData.currentBarcode,
            };

            var resp = await getSummaryRequest(objectToQueryString(params, true));

            if (resp.success) {
                setBooks(resp.data.books.filter((x) => x.isEnabled));
                setDates(resp.data.dates);
                setBarcode(resp.data.barcode);
                setLoading(false);
            }
        }
    }

    /**
     * Updates the books information and fetches the summary if the update is successful.
     *
     * @param {Function} setBooks - Function to update the books state.
     * @param {Function} setDates - Function to update the dates state.
     * @param {Function} setBarcode - Function to update the barcode state.
     * @param {Function} setLoading - Function to update the loading state.
     * @param {Array} books - Array of book objects to be updated.
     * @returns {Promise<void>} - A promise that resolves when the update is complete.
     */
    async function updateBooks(setBooks, setDates, setBarcode, setLoading, books) {
        setLoading(true);
        const updateBooks = books.map((item) => ({
            isbn: item.book.isbn,
            barcode: item.barcode,
            state: item.state,
            reportType: hasValue(item.reportType) ? parseInt(item.reportType) : null,
        }));
        const data = await booksUpdateMany(updateBooks);
        if (data.success) {
            getSummary(setBooks, setDates, setBarcode, setLoading);
        }
    }

    /**
     * Completes the book processing by sending a request with the current barcode and updates the state accordingly.
     *
     * @param {Function} setBooks - Function to update the books state.
     * @param {Function} setDates - Function to update the dates state.
     * @param {Function} setBarcode - Function to update the barcode state.
     * @param {Function} setLoading - Function to update the loading state.
     * @returns {Promise<void>} - A promise that resolves when the book processing is complete.
     */
    async function completeBooks(setBooks, setDates, setBarcode, setLoading) {
        setLoading(true);
        const params = {
            barcode: userData.currentBarcode,
        };

        const completeResp = await booksCompleteRequest(objectToQueryString(params, true));
        if (completeResp.success) {
            getSummary(setBooks, setDates, setBarcode, setLoading);
        }
    }

    /**
     * Updates the book information and refreshes the summary.
     *
     * @param {Array} books - The list of books to be updated.
     * @param {Function} setBooks - Function to update the state of books.
     * @param {Function} setDates - Function to update the state of dates.
     * @param {Function} setBarcode - Function to update the state of barcode.
     * @param {Function} setLoading - Function to set the loading state.
     * @returns {Promise<void>} - A promise that resolves when the update is complete.
     */
    async function updateBook(books, setBooks, setDates, setBarcode, setLoading) {
        setLoading(true);
        const updateBooks = books.map((item) => ({
            isbn: item.book.isbn,
            barcode: item.barcode,
            state: item.state,
            reportType: hasValue(item.reportType) ? parseInt(item.reportType) : null,
        }));
        const data = await booksUpdateMany(updateBooks);
        if (data.success) {
            getSummary(setBooks, setDates, setBarcode, setLoading);
        }
    }



    return {
        checkNotification,
        getSummary,
        updateBooks,
        completeBooks,
        updateBook,
    };
}

export default StudentHomePageViewModel;